import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useUnresolvedAlerts } from "@/shared/api/hooks/alerts";
import { useSites } from "@/shared/api/hooks/sites";
import { qk } from "@/shared/api/keys";
import { useOrgAlertChannel } from "@/shared/api/ws";
import { selectUser, useAuth } from "@/shared/auth/store";
import { useAlarmSettings } from "@/shared/hooks/useAlarmSettings";
import { isSilentHealth } from "@/shared/i18n/alertPhrases";
import { cancelBroadcastForAlerts, pruneBroadcast, useAlertTTS } from "@/shared/hooks/useAlertTTS";
import { isBaselineExempt, isPushFocusEligible } from "@/shared/lib/alertBaseline";
import { isUnresolved } from "@/shared/lib/alertScope";
import { onPushFocus, peekPushFocus } from "@/shared/lib/pushFocus";
import { isAlertWsDown, useAlertLinkStore } from "@/shared/state/alertLinkStore";

/**
 * 한 번에 발화할 수 있는 경보 수. 낭독은 <b>전역 FIFO 큐 한 개</b>이고(useAlertTTS
 * `broadcastQueue`), 한 건이 선택된 언어 수만큼 반복되며 항목마다 수 초가 걸린다.
 * 큐에 12건을 밀어 넣으면 그 뒤에 <b>진짜로 새로 터진 경보</b>가 수 분간 발화되지 못한다.
 * 그래서 개수로 제한한다 — 「언제 발생했나」는 묻지 않으므로 아직 새고 있는 오래된
 * 경보를 죽이지 않는다.
 */
const MAX_ANNOUNCE_PER_TICK = 3;

/**
 * Headless app-wide announcer. Mounted once in <AppShell> so new alerts
 * are read aloud (when TTS is enabled) on every page, not just the alerts
 * view.
 *
 * Source of truth is `useUnresolvedAlerts()` — 미해제 경보 전체(무필터 1페이지 +
 * state=open/acknowledged 각 500건의 합집합), 30초 폴링. 무필터 1페이지만 보면
 * 경보가 몰릴 때 아직 해제되지 않은 경보가 50건 밖으로 밀려 목록에서 사라지고,
 * 그러면 음성도 같이 사라진다.
 *
 * 여기에 더해 조직 경보 채널을 구독해 새 경보가 발령되는 즉시 그 질의를 무효화한다.
 * 해제도 `alert.cleared` 로 실시간으로 온다(2026-10-01 — 자동·수동 해제 모두, safety/alerts/dispatcher.py
 * `on_alert_cleared`). 그때 그 경보의 낭독을 지금 읽는 문장까지 바로 거둔다. 30초 폴링은 WS 가 끊겼을 때의
 * 보조 경로로 남는다. Dedupe lives in `useAlertTTS` (per alert id), so re-renders
 * and overlapping mounts never double-speak.
 */
export function AlertTtsAnnouncer() {
  const { announce, announceCleared } = useAlertTTS();
  const qc = useQueryClient();
  const user = useAuth(selectUser);
  const orgId = user?.organization?.id ?? null;

  const { alerts, ready } = useUnresolvedAlerts();
  const { data: sitesData } = useSites();

  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    (sitesData?.results ?? []).forEach((s) => {
      if (s.id != null) m.set(s.id, s.name);
    });
    return (id: number | string) => m.get(Number(id)) ?? "";
  }, [sitesData]);

  /**
   * 이 세션이 시작될 때 <b>이미 목록에 있던</b> 경보 id — <b>기준선</b>. 「경보 목록을 처음
   * 받았다」는 「경보가 지금 발령됐다」가 아니므로 읽지 않고 여기에만 담는다. 이후 목록에
   * 새로 나타나는 id 만 발화 후보가 된다.
   *
   * <p>⚠️ 2026-08-31 사고: 음성경보 기본값을 켜자 화면을 열 때마다 <b>과거 경보 12건이 줄줄이
   * 낭독</b>됐다. 며칠 전(7월) 것까지 포함이었다. 원인은 이 컴포넌트가 목록이 바뀔 때마다
   * <b>목록 전체</b>를 넘기고, 중복방지 Set 은 훅 안에 있어 <b>새로고침하면 비기</b> 때문이다.
   * 「경보가 발령됐다」와 「경보 목록을 처음 받았다」는 전혀 다른 사건인데 구분이 없었다.
   * <b>그 사고를 막는 것은 이 기준선이다</b> — 첫 응답은 통째로 흡수되어 한 건도 발화되지
   * 않는다. 여기에 「한 번에 최대 {@link MAX_ANNOUNCE_PER_TICK}건」이라는 개수 상한이
   * 두 번째 벨트로 걸린다.</p>
   *
   * <p>⚠️ 함께 있던 「발생 후 10분 이내만 읽는다」는 시간창은 <b>제거했다</b>. 미해제 경보는
   * 오래됐다고 덜 위험하지 않다 — 폴링이 40분 멈췄다 복귀하면 35분 전에 시작돼 <b>아직 새고
   * 있는</b> 누출이 창 밖이라는 이유로 침묵하고, 그대로 기준선에 흡수되어 영구히 안 읽혔다.
   * 게다가 그 판정은 <b>기기 시계</b>에 걸려 있었다: 시계가 20분 빠르면 모든 경보가 창 밖이라
   * 음성 경보가 통째로 영구 침묵하고, 느리면 부호가 뒤집혀 백로그까지 전부 낭독한다(=위
   * 사고의 재현). RTC 없는 키오스크·RPi 콘솔이 실제 배치 대상이라 이론이 아니다.
   * 그래서 이 컴포넌트는 발화 여부 판정에 <b>시각을 일절 쓰지 않는다</b>.</p>
   *
   * <p>예외(2026-10-19, 10-12 검증 V-22): 기준선을 잡을 때 푸시 알림으로 고른 경보는 흡수하지 않는다(시각 무관) —
   * 아직 미확인(open)일 때만이다(10-20: 확인된 경보는 경보창이 안 뜨므로 소리도 내지 않는다). 기본 끔 설정 「다시 열 때 진행 중 경보 알림」을 켠 단말만 최근 N분 판정에 기기 시계를 쓰며, 시계가 틀리면
   * 예외가 줄어드는 쪽으로 기운다(lib/alertBaseline.ts).</p>
   */
  const seen = useRef<Set<number> | null>(null);
  /**
   * 기준선이 **흡수만 하고 읽지 않은** 경보 id(첫 목록). 나중에 푸시 알림으로 그 경보를 고르면(그때 아직 미확인이면)
   * 여기서 빼고 `seen` 에서도 빼 다시 후보로 만든다(10-12 검증 V-22). 이미 낭독한 경보는 여기 없으므로 두 번 읽지 않는다.
   */
  const absorbed = useRef<Set<number>>(new Set());
  /**
   * 앱이 살아 있는 동안 들어온 푸시 탭(경보 id) — 아래 효과가 **그때의 목록에서 그 경보가 아직 미확인(open)인지**
   * 보고 흡수를 푼다. 탭 처리기에서 바로 풀면 경보 상태를 볼 수 없어, 이미 확인된 경보도 사이렌·「즉시 대피」를
   * 다시 냈다(경보창은 open 만 띄워 창은 안 뜸 — 10-20 F1 검증 잔존 결함 1).
   */
  const pushTaps = useRef<Set<number>>(new Set());
  /** 푸시 알림으로 고른 경보 — 콜드 스타트면 맡겨 둔 것을 꺼내고, 앱이 살아 있으면 이벤트로 받는다. */
  const [focusId, setFocusId] = useState<number | null>(() => peekPushFocus());
  /** 「다시 열 때 진행 중 경보 알림」(기본 끔) — 기준선을 잡는 그 순간의 값만 쓴다. */
  const reshowRecent = useAlarmSettings((s) => s.reshowRecent);
  /**
   * 이 세션에서 **실제로 낭독한** 경보(문장 묶음 대표) — 해제 안내용. 같은 문장 묶음의 id 전부를
   * 대표 한 건에 걸어 두고, 묶음이 **전부** 목록에서 빠지면 해제 안내를 한 번 읽는다.
   */
  const announced = useRef<Map<string, { rep: Parameters<typeof announce>[0][number]; ids: Set<number> }>>(new Map());

  // 앱이 살아 있는 동안 들어온 푸시 탭 — 맡겨 두고(pushTaps) 효과를 다시 돌린다. 흡수를 풀지는 효과가 경보 상태를 보고 정한다.
  useEffect(
    () =>
      onPushFocus((id) => {
        pushTaps.current.add(id);
        setFocusId(id);
      }),
    [],
  );

  // Push newly appeared unresolved alerts into the announcer.
  useEffect(() => {
    // 질의가 최초 1회 끝나기 전에는 아무것도 하지 않는다. ⚠️ 예전에는 `!alerts.length` 로
    // 되돌아갔는데, 그러면 경보 0건인 상태로 앱을 켰을 때 기준선이 잡히지 않고 그 뒤
    // <b>처음 터진 진짜 경보 1건이 기준선으로 흡수되어</b> 영구 침묵했다.
    if (!ready) return;

    const items = alerts
      // 경광등 끊김은 읽지 않는다(2026-11-04 「방송 안 함」) — 화면·OS 알림 글로만(alertPhrases.isSilentHealth).
      .filter((a) => a.id != null && isUnresolved(a) && !isSilentHealth(a.kind, a.subject_key))
      .map((a) => ({
        id: a.id!,
        level: a.level ?? 0,
        site: a.site,
        gas: a.gas,
        kind: a.kind,
        is_drill: a.is_drill,
        subject_key: a.subject_key,
      }));

    if (seen.current === null) {
      // 목록이 비어 있어도 여기서 기준선을 확정한다(빈 Set).
      // 기준선 예외(lib/alertBaseline.ts): 푸시로 고른 경보, 그리고 설정을 켰으면 최근 미확인 경계 이상 —
      // 이것들만 기준선에 넣지 않고 바로 아래에서 「새 경보」로 읽는다. 나머지는 종전대로 흡수·침묵.
      const now = Date.now();
      const exempt = new Set(
        alerts
          .filter((a) => a.id != null && isBaselineExempt(a, { focusId, reshowRecent, now }))
          .map((a) => a.id!),
      );
      seen.current = new Set();
      for (const i of items) {
        if (exempt.has(i.id)) continue;
        seen.current.add(i.id);
        absorbed.current.add(i.id);
      }
      if (!exempt.size) return;
    }

    // 앱이 살아 있는 동안 들어온 푸시 탭 — 기준선이 흡수만 했던 경보가 **지금 미확인(open)** 이면 다시 후보로 돌린다
    // (콜드 스타트 예외와 같은 판정, lib/alertBaseline.ts). 이미 확인(acknowledged)된 경보는 흡수된 채 둔다 — 화면 이동만.
    // 목록에 없는 id(해제됨·아직 안 받음)는 흡수된 적도 없거나 더 읽을 것이 없다.
    if (pushTaps.current.size) {
      for (const id of pushTaps.current) {
        const a = alerts.find((x) => x.id === id);
        if (a && isPushFocusEligible(a) && absorbed.current.delete(id)) seen.current.delete(id);
      }
      pushTaps.current.clear();
    }

    // 해제 반영(2026-09-28 음원 점검): ① 해제된 경보의 아직 안 읽은 언어분을 큐에서 뺀다
    // ② 낭독했던 문장 묶음이 전부 해제되면 주 언어로 「○○ 경보가 해제되었습니다」 한 번.
    const live = new Set(items.map((i) => i.id));
    pruneBroadcast((id) => live.has(id));
    for (const [key, g] of announced.current) {
      if ([...g.ids].some((id) => live.has(id))) continue;
      announced.current.delete(key);
      announceCleared(g.rep, siteName);
    }

    const fresh = items.filter((i) => !seen.current!.has(i.id));
    if (!fresh.length) return;

    // ⛔ 기준선(`seen`)은 **실제로 읽은 것**에만 찍는다. 상한 판정 **전에** fresh 전부를
    //    찍으면, 상한에 잘린 건이 다시는 후보가 되지 않아 **영구히 안 읽힌다**
    //    (2026-09-03 검증에서 3개 렌즈가 독립적으로 같은 지적을 했다).
    //    「이미 읽었다」와 「이번엔 순서가 밀렸다」는 다른 사건이다.
    //    잘린 건은 미해제인 한 다음 폴링 목록에 그대로 남으므로 재시도가 성립한다.

    // 주의(1단계)는 낭독 대상이 아니다 — 설정(주의 알림음, 기본 끔)이 켜져 있으면 짧은 알림음만 한 번.
    // 설비 경보(kind=health — 센서 판독 모순 K 「sensor:{node}:{gas}」 유지보수 알림)는 알림음도 내지 않는다(useAlertTTS
    // chimeCaution 판정, 10-20). 그래도 announce 에 넘겨 공유 중복 방지(spoken)에는 찍는다.
    // 해제 안내(announced)에는 넣지 않는다(읽지 않은 경보의 「해제되었습니다」를 말하지 않게).
    const cautions = fresh.filter((i) => i.level === 1);
    if (cautions.length) announce(cautions, siteName);

    // 발화 대상이 아닌 등급은 지금 확정한다 — 다시 후보가 될 이유가 없다.
    for (const i of fresh) if (i.level < 2) seen.current!.add(i.id);

    // 같은 문장이 되는 건은 한 번만 읽는다. `buildAlertPhrase` 인자가 (등급·가스·현장)
    // 뿐이라 이 셋이 같으면 발화 문장이 글자 그대로 같다 — 12개 지점이 한 현장에서
    // 동시에 울려도 발화는 1건이다(노드는 문장에 들어가지 않으므로 손실이 없다).
    const byPhrase = new Map<string, (typeof fresh)[number][]>();
    for (const i of fresh) {
      if (i.level < 2) continue;
      // 문장을 가르는 값 전부 — 종류(가스/설비 무응답)·훈련 여부·무응답 대상이 다르면 다른 문장이다.
      //   (빠지면 실경보가 같은 등급·현장의 훈련 경보 묶음에 흡수돼 「훈련 상황입니다」로 읽힐 수 있다.)
      const subj = i.kind === "health" ? (i.subject_key ?? "").split(":")[0] : "";
      const key = `${i.level}|${i.gas ?? ""}|${i.site ?? ""}|${i.kind ?? "gas"}|${i.is_drill ? 1 : 0}|${subj}`;
      const g = byPhrase.get(key);
      if (g) g.push(i);
      else byPhrase.set(key, [i]);
    }

    // 심각도 높은 순. 동급은 `alerts` 의 순서(최신순)가 그대로 유지된다(안정 정렬).
    const groups = [...byPhrase.values()].sort((a, b) => b[0].level - a[0].level);
    const batch = groups.slice(0, MAX_ANNOUNCE_PER_TICK);

    // 읽기로 한 문장에 속한 건만 기준선에 넣는다. 잘린 문장은 남겨 다음 틱에서 다시 읽는다.
    for (const g of batch) for (const i of g) seen.current!.add(i.id);
    for (const g of batch) {
      const rep = g[0]!;
      const key = `${rep.id}`;
      announced.current.set(key, { rep, ids: new Set(g.map((i) => i.id)) });
    }
    // 묶음의 id 전부를 대표에 실어 보낸다 — 묶음이 **전부** 해제될 때만 낭독을 끊는다.
    if (batch.length) announce(batch.map((g) => ({ ...g[0]!, groupIds: g.map((i) => i.id) })), siteName);
    // `focusId`·`reshowRecent` 는 기준선을 잡는 첫 실행에서만 읽는다. focusId 는 푸시 탭 때 이 효과를 다시 돌리려고 둔다.
  }, [alerts, ready, announce, announceCleared, siteName, focusId, reshowRecent]);

  // Refresh the alert list the moment a new alert fires server-side.
  // ⚠️ 예전에는 `useAlertChannel(orgId, …)` 였는데, 그 훅은 `site:<id>:alerts` 채널을
  // 만든다(shared/api/ws.ts). 조직 id 를 사이트 채널에 꽂은 것이라 서버가 4403 으로
  // 끊었고 재접속도 막혀(4403 은 백오프 제외) 이 실시간 갱신은 죽어 있었다.
  // 조직 채널 훅으로 바꾼다. 이 낭독기는 WS 없이 30초 폴링만으로도 성립한다.
  const wsStatus = useOrgAlertChannel<{ id?: number }>(orgId, (env) => {
    if (env.type === "alert.fired") {
      qc.invalidateQueries({ queryKey: qk.safety.alerts.all });
    } else if (env.type === "alert.cleared") {
      // 해제 — 그 경보의 낭독을 지금 바로 멈추고(다른 경보 낭독은 이어진다), 목록도 새로 받는다.
      const id = Number(env.payload?.id);
      if (Number.isFinite(id) && id > 0) cancelBroadcastForAlerts([id]);
      qc.invalidateQueries({ queryKey: qk.safety.alerts.all });
    }
  });

  // WS 상태를 경보 폴링에 알린다(2026-10-19, 10-12 검증 V-31) — 끊긴 동안 경보 질의 간격을 30초 → 10초로 줄인다
  // (shared/api/hooks/alerts.ts). 다시 붙으면 끊긴 사이 놓친 발령·해제를 한 번 새로 받는다.
  // 이 컴포넌트는 AppShell 에 늘 하나 떠 있어 조직 채널 상태를 적는 자리로 맞다(경보창은 꺼질 수 있다).
  const setWsStatus = useAlertLinkStore((s) => s.setWsStatus);
  const wasDown = useRef(false);
  useEffect(() => {
    setWsStatus(orgId ? wsStatus : null);
    const down = isAlertWsDown(useAlertLinkStore.getState());
    if (wasDown.current && wsStatus === "open") qc.invalidateQueries({ queryKey: qk.safety.alerts.all });
    wasDown.current = down;
  }, [orgId, wsStatus, setWsStatus, qc]);
  useEffect(() => () => setWsStatus(null), [setWsStatus]);

  return null;
}
